import { __awaiter } from '../../../../../../_virtual/_tslib.js';
import React, { useState, useContext, useMemo, useEffect } from 'react';
import { usePopper } from 'react-popper';
import { StyledApplicationSwitcher, StyledBarDropDownMenu } from './ApplicationSwitcher.styles.js';
import { useSelect } from 'downshift';
import { useWindowWidth } from '@lwt-helix/useWindowWidth';
import { NavbarContext } from './NavbarContext.js';
import { NavbarHelixIcon } from './NavbarHelixIcon.js';
import ApplicationSwitcherMobilePanel from './ApplicationSwitcherMobilePanel.js';
import { useFocusManager } from '@react-aria/focus';
import ApplicationSwitcherMenuItem from './ApplicationSwitcherMenuItem.js';
import ApplicationSwitcherLaunchpadItem from './ApplicationSwitcherLaunchpadItem.js';

const ApplicationSwitcher = ({ onSelectedItemChanged, applicationName = 'TransactionDesk', className, dataLwtId = 'navbar-application-switcher', appSections, linkWrapper, launchpadUrl, preventItemClick }) => {
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [allSectionItems, setAllSectionItems] = useState([]);
    const { windowWidth } = useWindowWidth();
    const { isDark, setAppSwitcherItems, showMobileApplicationSwitcher, setShowMobileApplicationSwitcher, appInstancesOpen, setAppInstancesOpen, setAppSwitcherLinkWrapper, setLaunchpadUrl, appSwitcherItems } = useContext(NavbarContext);
    const focusManager = useFocusManager();
    const handleKeyDown = (e) => {
        if (!focusManager)
            return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                focusManager.focusNext({ wrap: true });
                break;
            case 'ArrowUp':
                e.preventDefault();
                focusManager.focusPrevious({ wrap: true });
                break;
            case 'End':
                e.preventDefault();
                focusManager.focusLast({});
                break;
            case 'Home':
                e.preventDefault();
                focusManager.focusFirst({});
                break;
        }
    };
    const modifiers = useMemo(() => [
        {
            name: 'offset',
            options: {
                offset: [0, 8]
            }
        },
        {
            name: 'flip',
            enabled: false
        }
    ], []);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers
    });
    const stateReducer = (state, actionAndChanges) => {
        const { type, changes } = actionAndChanges;
        switch (type) {
            case useSelect.stateChangeTypes.MenuKeyDownArrowDown: {
                const selectedItemNode = document.querySelectorAll('.helix-unified-nav__application-switcher-section-list li');
                // filter out lis with className section-header
                const selectedItemNodeArray = Array.from(selectedItemNode).filter((child) => !child.classList.contains('section-header'))[state.highlightedIndex];
                const appItem = allSectionItems[state.highlightedIndex];
                // if appItem has appInstances, then open the appInstances menu
                if (appItem &&
                    appItem.appInstances &&
                    appItem.appInstances.length > 0 &&
                    appInstancesOpen.length === 0) {
                    // focus on the button with the class .helix-app-switcher__item-badge-dropdown
                    const button = selectedItemNodeArray.querySelector('.helix-app-switcher__item-badge-dropdown');
                    if (button) {
                        button.focus();
                        button.click();
                    }
                    return {
                        isOpen: true,
                        highlightedIndex: state.highlightedIndex
                    };
                }
                if (appInstancesOpen.length > 0) {
                    return {
                        isOpen: true,
                        highlightedIndex: state.highlightedIndex
                    };
                }
                const highlightedIndex = state.highlightedIndex + 1;
                if (highlightedIndex === allSectionItems.length) {
                    return Object.assign(Object.assign({}, changes), { isOpen: true, highlightedIndex: 0 });
                }
                return Object.assign(Object.assign({}, changes), { isOpen: true });
            }
            case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                if (state.highlightedIndex === 0) {
                    return Object.assign(Object.assign({}, changes), { isOpen: true, highlightedIndex: allSectionItems.length - 1 });
                }
                if (appInstancesOpen && appInstancesOpen.length > 0) {
                    return {
                        isOpen: true,
                        highlightedIndex: state.highlightedIndex
                    };
                }
                return Object.assign(Object.assign({}, changes), { isOpen: true });
            case useSelect.stateChangeTypes.MenuKeyDownEnter: {
                const appItem = allSectionItems[state.highlightedIndex];
                if (appItem && appItem.onClick && appInstancesOpen && appInstancesOpen.length === 0)
                    appItem.onClick();
                if (appItem && appItem.url && appItem.url !== '') {
                    // find the li with the class .highlighted and click on the anchor tag
                    const selectedItemNode = document.querySelectorAll('.helix-unified-nav__application-switcher-section-list li');
                    // filter out lis with className section-header
                    const selectedItemNodeArray = Array.from(selectedItemNode).filter((child) => !child.classList.contains('section-header'))[state.highlightedIndex];
                    if (selectedItemNodeArray) {
                        const anchor = selectedItemNodeArray.querySelector('a');
                        if (anchor) {
                            anchor.click();
                        }
                    }
                    const launchpadItem = document.querySelector('.helix-unified-nav__application-switcher-section--launchpad');
                    if (launchpadItem && appItem.label === 'Launchpad') {
                        const launchpadLink = launchpadItem.querySelector('a');
                        if (launchpadLink) {
                            launchpadLink.click();
                        }
                    }
                }
                if (state.highlightedIndex !== null &&
                    appItem &&
                    appItem.appInstances &&
                    appItem.appInstances.length > 0) {
                    return Object.assign(Object.assign({}, changes), { highlightedIndex: state.highlightedIndex, isOpen: true });
                }
                return Object.assign({}, changes);
            }
            case useSelect.stateChangeTypes.ItemClick: {
                const appItem = allSectionItems[state.highlightedIndex];
                if (appItem && preventItemClick && preventItemClick(appItem)) {
                    // if the item icludes an a tag, prevent the default action
                    const selectedItemNode = document.querySelectorAll('.helix-unified-nav__application-switcher-section-list li');
                    // filter out lis with className section-header
                    const selectedItemNodeArray = Array.from(selectedItemNode).filter((child) => !child.classList.contains('section-header'))[state.highlightedIndex];
                    if (selectedItemNodeArray) {
                        const anchor = selectedItemNodeArray.querySelector('a');
                        if (anchor) {
                            // get the anchor element and preventDefault and stop propagation
                            // set the href to javascript:void(0)
                            anchor.href = 'javascript:void(0)';
                            // then after the event is done, set the href back to the original value
                            setTimeout(() => {
                                anchor.href = appItem.url ? appItem.url : '';
                            }, 0);
                        }
                    }
                    return {
                        isOpen: true,
                        highlightedIndex: -1,
                        selectedItem: null
                    };
                }
                if (appItem && appItem.onClick && appInstancesOpen && appInstancesOpen.length === 0)
                    appItem.onClick();
                if (state.highlightedIndex !== null &&
                    appItem &&
                    appItem.appInstances &&
                    appItem.appInstances.length > 0) {
                    return Object.assign(Object.assign({}, changes), { highlightedIndex: state.highlightedIndex, isOpen: true });
                }
                return Object.assign({}, changes);
            }
            case useSelect.stateChangeTypes.MenuBlur: {
                const appItem = allSectionItems[state.highlightedIndex];
                if (state.highlightedIndex !== null &&
                    appItem &&
                    appItem.appInstances &&
                    appItem.appInstances.length > 0) {
                    return Object.assign(Object.assign({}, changes), { highlightedIndex: state.highlightedIndex, isOpen: true });
                }
                return Object.assign({}, changes);
            }
            case useSelect.stateChangeTypes.MenuKeyDownEscape: {
                if (appInstancesOpen && appInstancesOpen.length > 0) {
                    const menu = popperElement === null || popperElement === void 0 ? void 0 : popperElement.childNodes[0];
                    menu.focus();
                    return Object.assign(Object.assign({}, changes), { highlightedIndex: state.highlightedIndex, isOpen: true });
                }
                return Object.assign(Object.assign({}, changes), { isOpen: false });
            }
            default:
                return changes; // otherwise business as usual.
        }
    };
    const itemToString = (item) => (item.label ? item.label : '');
    const switchAppSectionTypes = () => __awaiter(void 0, void 0, void 0, function* () {
        return typeof appSections === 'function' ? yield appSections() : appSections;
    });
    const getAppItems = () => __awaiter(void 0, void 0, void 0, function* () {
        const sectionItems = [];
        const appSectionType = yield switchAppSectionTypes();
        appSectionType.map((item) => {
            item.sectionItems.map((sectionItem) => {
                sectionItems.push(sectionItem);
            });
        });
        if (launchpadUrl) {
            sectionItems.push({
                label: 'Launchpad',
                url: launchpadUrl
            });
        }
        setAllSectionItems(sectionItems);
    });
    const updateAppSwitcherItems = () => __awaiter(void 0, void 0, void 0, function* () {
        const items = yield switchAppSectionTypes();
        setAppSwitcherItems(items);
    });
    const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex, setHighlightedIndex } = useSelect({
        items: allSectionItems,
        itemToString,
        stateReducer,
        defaultHighlightedIndex: -1
    });
    const handleClickOutsideMobile = (e) => {
        if (showMobileApplicationSwitcher) {
            if (e.target.closest('.helix-unified-nav__application-switcher')) {
                return;
            }
            setShowMobileApplicationSwitcher(false);
        }
    };
    const handleKeyDownWindow = (e) => {
        if (e.key === 'Escape') {
            if (showMobileApplicationSwitcher) {
                setShowMobileApplicationSwitcher(false);
            }
        }
    };
    useEffect(() => {
        if (appSections) {
            updateAppSwitcherItems();
            getAppItems();
        }
    }, [appSections]);
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDownWindow);
        window.addEventListener('click', handleClickOutsideMobile);
        return () => {
            window.removeEventListener('keydown', handleKeyDownWindow);
            window.removeEventListener('click', handleClickOutsideMobile);
        };
    }, [showMobileApplicationSwitcher]);
    useEffect(() => {
        setAppSwitcherLinkWrapper(() => linkWrapper);
    }, [linkWrapper]);
    useEffect(() => {
        if (launchpadUrl) {
            setLaunchpadUrl(launchpadUrl);
        }
    }, [launchpadUrl]);
    useEffect(() => {
        if (!isOpen) {
            setAppInstancesOpen([]);
        }
        update && update();
    }, [isOpen, update]);
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledApplicationSwitcher, { className: `helix-unified-nav__application-switcher ${className ? className : ''}`, "$isDark": isDark, "data-lwt-id": dataLwtId },
            windowWidth < 576 && React.createElement(ApplicationSwitcherMobilePanel, { applicationName: applicationName }),
            React.createElement("div", { className: 'helix-unified-nav__application-name', ref: setReferenceElement, style: { display: windowWidth < 576 ? 'none' : 'block' } },
                React.createElement("button", Object.assign({}, getToggleButtonProps({
                    onKeyDown: (e) => handleKeyDown(e)
                }), { className: `helix-unified-nav__navigation-item ${isOpen ? 'active' : ''}`, style: { position: 'relative' } }),
                    React.createElement("span", { className: 'helix-unified-nav__application-switcher-product-name' }, applicationName),
                    React.createElement(NavbarHelixIcon, { iconName: 'dropdown_open' }))),
            React.createElement(StyledBarDropDownMenu, Object.assign({ ref: setPopperElement, isOpen: isOpen, style: styles.popper, "$numberOfColumns": appSections.length }, attributes.popper),
                React.createElement("ul", Object.assign({ className: 'helix-unified-nav__application-switcher-section-list' }, getMenuProps({
                    onKeyDown: (e) => __awaiter(void 0, void 0, void 0, function* () {
                        switch (e.key) {
                            case 'ArrowRight':
                                e.preventDefault();
                                if (windowWidth < 576) {
                                    return false;
                                }
                                appSwitcherItems.map((section, sectionIndex) => {
                                    const itemIndex = section.sectionItems.indexOf(allSectionItems[highlightedIndex]);
                                    if (itemIndex > -1) {
                                        const rightItemIndex = allSectionItems.indexOf(appSections[sectionIndex + 1]
                                            ? appSections[sectionIndex + 1].sectionItems[itemIndex]
                                            : 0);
                                        if (rightItemIndex > -1) {
                                            setHighlightedIndex(rightItemIndex);
                                        }
                                        else {
                                            const nextColumnIndex = allSectionItems.indexOf(appSections[0].sectionItems[itemIndex]);
                                            if (nextColumnIndex > -1) {
                                                // If there is no item directly on the right
                                                //  select the last item in the next column
                                                if (nextColumnIndex === highlightedIndex) {
                                                    const nextColumnFirstItemIndex = allSectionItems.indexOf(appSections[sectionIndex + 1].sectionItems[appSections[sectionIndex + 1].sectionItems.length -
                                                        1]);
                                                    setHighlightedIndex(nextColumnFirstItemIndex);
                                                }
                                                else {
                                                    // Select the item item in the first column with the same
                                                    //  position of the item that the key down event happened on
                                                    setHighlightedIndex(nextColumnIndex);
                                                }
                                            }
                                            else {
                                                // If there are no items in the first
                                                // column at the same position
                                                // select the first item in the first column
                                                const firstItemLastRow = allSectionItems.indexOf(appSections[0].sectionItems[0]);
                                                // Select the first item in the first column
                                                setHighlightedIndex(firstItemLastRow);
                                            }
                                        }
                                    }
                                });
                                break;
                            case 'ArrowLeft':
                                e.preventDefault();
                                if (windowWidth < 576) {
                                    return false;
                                }
                                appSwitcherItems.map((section, sectionIndex) => {
                                    const itemIndex = section.sectionItems.indexOf(allSectionItems[highlightedIndex]);
                                    if (itemIndex > -1) {
                                        const leftItemIndex = allSectionItems.indexOf(appSections[sectionIndex - 1]
                                            ? appSections[sectionIndex - 1].sectionItems[itemIndex]
                                            : 0);
                                        if (leftItemIndex > -1) {
                                            setHighlightedIndex(leftItemIndex);
                                        }
                                        else {
                                            const lastColumnIndex = allSectionItems.indexOf(appSections[appSections.length - 1].sectionItems[itemIndex]);
                                            if (lastColumnIndex > -1) {
                                                // If there is no item directly on the left
                                                //  select the last item in the next column
                                                if (lastColumnIndex === highlightedIndex) {
                                                    const nextColumnFirstItemIndex = allSectionItems.indexOf(appSections[sectionIndex - 1].sectionItems[appSections[sectionIndex - 1].sectionItems.length -
                                                        1]);
                                                    setHighlightedIndex(nextColumnFirstItemIndex);
                                                }
                                                else {
                                                    // Select the item item in the last column with the
                                                    //  same position of the item that the key
                                                    //  down event happened on
                                                    setHighlightedIndex(lastColumnIndex);
                                                }
                                            }
                                            else {
                                                // If there are no items in the last column at the same position
                                                // select the last item in the last column
                                                const firstItemLastRow = allSectionItems.indexOf(appSections[appSections.length - 1].sectionItems[appSections[appSections.length - 1].sectionItems
                                                    .length - 1]);
                                                setHighlightedIndex(firstItemLastRow);
                                            }
                                        }
                                    }
                                });
                                break;
                        }
                    })
                })),
                    React.createElement("div", { className: 'helix-unified-nav__application-switcher-sections-wrapper' }, appSwitcherItems.map((section, sectionIndex) => (React.createElement("ul", { key: sectionIndex, className: 'helix-unified-nav__application-switcher-section' },
                        section.sectionName && React.createElement("li", { className: 'section-header' }, section.sectionName),
                        section.sectionItems.map((item, index) => {
                            return (React.createElement(ApplicationSwitcherMenuItem, { setHighlightedIndex: setHighlightedIndex, menuRef: popperElement, key: `${item.label}${sectionIndex}${index}`, getAppItems: () => allSectionItems, item: item, appSections: appSwitcherItems, highlightedIndex: highlightedIndex, sectionIndex: sectionIndex, getItemProps: getItemProps, "data-item-index": index }));
                        }))))),
                    launchpadUrl && (React.createElement("div", Object.assign({ className: `helix-unified-nav__application-switcher-section--launchpad ${allSectionItems.length - 1 === highlightedIndex ? 'highlighted' : ''}` }, getItemProps({
                        item: {
                            label: 'Manage'
                        },
                        index: allSectionItems.length > 0 ? allSectionItems.length - 1 : 0
                    })),
                        React.createElement(ApplicationSwitcherLaunchpadItem, { numberOfColumns: appSections.length, url: launchpadUrl }))))))));
};
ApplicationSwitcher.displayName = 'ApplicationSwitcher';

export { ApplicationSwitcher as default };
//# sourceMappingURL=ApplicationSwitcher.js.map
